import { EditOutlined, ExportOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { getChatChannels } from '@app/api/channel.api';
import { getUserListWithQuery } from '@app/api/users.api';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Input } from '@app/components/common/inputs/Input/Input';
import { Spinner } from '@app/components/common/Spinner/Spinner.styles';
import { ADMIN_BASE_BATH } from '@app/constants/variables';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { IChannelBox } from '@app/interfaces/channel';
import { Badge, List, Tooltip } from 'antd';
import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useParams } from 'react-router-dom';
import * as S from './ChatInboxList.styles';
import { useDialog } from '@app/hooks/useDialog';
import { ChatInputFilterModal } from './ChatInputFilterModal/ChatInputFilterModal';
import { IUser } from '@app/interfaces/user';
import { isObject } from 'lodash';
import { firstLatterOfName, getDealRoomBuildingText, getUserFullName } from '@app/utils/utils';
import { setDealRoomChat } from '@app/store/slices/dealRoomSlice';

const PAGE_SIZE = 10;

export const ChatInboxList: React.FC = () => {
  const { dealRoomId } = useParams<{ dealRoomId: string }>();
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const dealRoomChat = useAppSelector((state) => state.dealRoom.dealRoomChat);
  const unreadMessagesByDealRoom = useAppSelector((state) => state.dealRoom.unreadMessages.unreadMessagesByDealRoom);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();
  const dispatch = useAppDispatch();

  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{ users: string[] }>({
    users: [],
  });
  const [channels, setChannels] = useState<IChannelBox[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<IUser[]>([]);

  const onChannelSelection = (channel: IChannelBox) => {
    navigate(`${ADMIN_BASE_BATH}/chats/${channel.dealRoom?._id}`);
  };

  const hasFilters = () => {
    if (filters?.users?.length > 0) return true;
    return false;
  };

  const onSearchSubmit = (values: { users: string[] }) => {
    setPage(1);
    setChannels([]);
    setFilters(values);
    handleDialogClose();
  };

  const openDealRoom = (dealRoom: string, key: string) => {
    if (!dealRoom) return;
    window.open(`${dealRoom}/page/${key}`, '_blank', 'noopener,noreferrer');
  };

  const viewDealRoom = (dealRoom: string) => {
    if (!dealRoom) return;
    window.open(`${ADMIN_BASE_BATH}/deal-rooms/deal-room-info/${dealRoom}`, '_blank', 'noopener,noreferrer');
  };

  const getQueryUsers = useCallback(async () => {
    try {
      const users = await getUserListWithQuery({
        projectBy: 'id,firstname,lastname,email,role',
      });
      setUsers(
        users.map((u) => {
          u.name = `${getUserFullName(u)} (${u.email}) (${t(
            u.role === process.env.REACT_APP_DEFAULT_ADMIN_ROLE ? 'common.manager' : 'common.worker',
          )})`;
          return u;
        }),
      );
    } catch (error) {}
  }, [t]);

  const onSearch = (e: { target: { value: SetStateAction<string> } }) => {
    setPage(1);
    setChannels([]);
    setSearch(e.target.value);
  };

  const loadMoreData = useCallback(
    async (page, loader = true) => {
      if (loader) setLoader(true);
      try {
        const res = await getChatChannels({
          users: filters?.users ? filters?.users?.join(',') : '',
          search,
          page: page,
          limit: PAGE_SIZE,
        });
        const resLength = res.length;
        if (resLength > 0) {
          setChannels((prevChannels) => {
            if (page === 1) {
              return res.concat(prevChannels).reduce((acc: IChannelBox[], current) => {
                if (!acc.find((item) => item._id === current._id)) {
                  acc.push(current);
                }
                return acc;
              }, []);
            } else {
              return [...prevChannels, ...res];
            }
          });
          setPage((prevPage) => prevPage + 1);
        } else if (page === 1) {
          setChannels([]);
        }
        setHasMore(resLength >= PAGE_SIZE);
      } catch (error) {
        notificationController.error({ message: (error as Error).message });
      } finally {
        if (loader) setLoader(false);
      }
    },
    [search, filters?.users],
  );

  const getDealRoomDescription = (channel: IChannelBox): JSX.Element => {
    if (!channel.dealRoom || !isObject(channel.dealRoom)) return <></>;
    const address = getDealRoomBuildingText(channel?.dealRoom);
    return (
      <>
        {address && (
          <S.Description>
            <strong>{t('chats.building')}</strong>: {address}
          </S.Description>
        )}
      </>
    );
  };

  const hasUnreadMessage = (channelId: string): boolean => {
    return unreadMessagesByDealRoom.some((item) => item._id === channelId && item.count > 0);
  };

  const validateScreenRefresh = useCallback(() => {
    loadMoreData(1, false);
  }, [loadMoreData]);

  useEffect(() => {
    loadMoreData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreData]);

  useEffect(() => {
    if (!isAdmin) return;
    getQueryUsers();
  }, [isAdmin, getQueryUsers]);

  useEffect(() => {
    if (dealRoomChat > 0) {
      validateScreenRefresh();
    }

    return () => {
      dispatch(setDealRoomChat(0));
    };
  }, [dealRoomChat, dispatch, validateScreenRefresh]);

  useEffect(() => {
    if (channels.length > 0 && !dealRoomId) {
      navigate(`${ADMIN_BASE_BATH}/chats/${channels[0].dealRoom?._id}`);
    }
  }, [navigate, channels, dealRoomId]);

  return (
    <>
      {loader && <Spinner spinning={loader} />}
      {dialogOpen && (
        <ChatInputFilterModal
          open={dialogOpen}
          onCancel={handleDialogClose}
          onSubmit={onSearchSubmit}
          values={filters}
          users={users}
          loader={loader}
        />
      )}
      <S.Header>
        <Input allowClear placeholder={t('chats.search')} prefix={<SearchOutlined />} onChange={onSearch} />
        {isAdmin && (
          <Tooltip title={t('chats.filters')}>
            <Button type={hasFilters() ? 'primary' : 'default'} onClick={handleDialogOpen}>
              <FilterOutlined size={40} />
            </Button>
          </Tooltip>
        )}
      </S.Header>
      <S.Wrapper id="scrollableDiv">
        <InfiniteScroll
          dataLength={channels.length}
          next={() => loadMoreData(page)}
          hasMore={hasMore}
          loader={<Spinner spinning={loader} />}
          scrollableTarget="scrollableDiv"
        >
          <List
            dataSource={channels}
            renderItem={(item) => (
              <List.Item
                key={item._id}
                onClick={() => onChannelSelection(item)}
                className={dealRoomId && dealRoomId === item?.dealRoom?._id ? 'active' : ''}
              >
                <List.Item.Meta
                  avatar={<S.AvatarWrapper size={40}>{firstLatterOfName(item.client?.id_num || '')}</S.AvatarWrapper>}
                  title={
                    <Badge dot={hasUnreadMessage(item._id)} offset={[-12, 7]} size="default">
                      <S.Title>
                        <span>
                          <strong>{t('chats.client')}</strong>: {item.client?.id_num}
                        </span>
                        {item.dealRoom && (
                          <>
                            <Tooltip title={t('chats.openDealRoom')}>
                              <ExportOutlined
                                onClick={() => {
                                  if (isObject(item.dealRoom)) {
                                    openDealRoom(item.dealRoom._id as string, item.dealRoom.key);
                                  }
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={t('chats.viewDealRoom')}>
                              <EditOutlined
                                onClick={() => {
                                  const dealRoomId = isObject(item.dealRoom) ? item.dealRoom._id : item.dealRoom;
                                  if (typeof dealRoomId === 'string') {
                                    viewDealRoom(dealRoomId);
                                  }
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      </S.Title>
                    </Badge>
                  }
                  description={getDealRoomDescription(item)}
                />
              </List.Item>
            )}
          />
        </InfiniteScroll>
      </S.Wrapper>
    </>
  );
};

export default ChatInboxList;
